import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { licenseAPI } from '../services/api';
import { AlertTriangle, Lock, Key, Send, CheckCircle2, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';

const LicenseBanner = () => {
  const { licenseScope, licenseStatus, isSuperAdmin, isTenantAdmin, user } = useAuth();
  const navigate = useNavigate();
  const [isRequesting, setIsRequesting] = useState(false);
  const [hasRequested, setHasRequested] = useState(false);

  const isOwner = isSuperAdmin || user?.type === 'SuperAdmin' || user?.email === 'superadmin@sanglob.local';
  const isLicenseLocked = Boolean(licenseScope?.isReadOnly || licenseScope?.isExpired);

  if (isOwner || !isLicenseLocked) return null;

  const handleRequestActivation = async () => {
    setIsRequesting(true);
    try {
      const res = await licenseAPI.requestActivation({
        reason: 'Tenant license is expired. Portal operating in Read-Only mode.'
      });
      if (res.data?.success) {
        setHasRequested(true);
        toast.success('Activation request submitted to your Tenant Administrator!');
      } else {
        toast.error(res.data?.message || 'Failed to submit activation request');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to request activation');
    } finally {
      setIsRequesting(false);
    }
  };

  return (
    <div className="bg-gradient-to-r from-red-600 via-rose-600 to-red-700 text-white px-4 sm:px-6 py-2 shadow-md relative z-[60] border-b border-red-700">
      <div className="w-full max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <div className="bg-white/20 p-1.5 rounded-full shrink-0">
            <Lock className="w-3.5 h-3.5 text-white" />
          </div>
          <div className="flex flex-wrap items-center gap-1.5 text-xs">
            <span className="font-extrabold uppercase tracking-wider bg-red-950/40 px-2 py-0.5 rounded border border-white/20">
              Read-Only Mode
            </span>
            <span className="hidden sm:inline text-red-200">•</span>
            <span className="font-medium text-red-50">
              {licenseStatus?.error || licenseStatus?.message || 'License has expired.'} Record creation & modifications are disabled.
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {isTenantAdmin ? (
            <button
              onClick={() => navigate('/settings/license')}
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold bg-white text-red-700 hover:bg-red-50 shadow-xs transition-all cursor-pointer"
            >
              <Key className="w-3 h-3 text-red-600" />
              <span>⚡ Activate License</span>
            </button>
          ) : (
            hasRequested ? (
              <span className="inline-flex items-center gap-1 text-xs text-emerald-200 bg-emerald-950/40 px-2 py-0.5 rounded border border-emerald-400/30">
                <CheckCircle2 className="w-3 h-3 text-emerald-300" />
                <span>Admin Notified</span>
              </span>
            ) : (
              <button
                onClick={handleRequestActivation}
                disabled={isRequesting}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold bg-white/20 hover:bg-white/30 text-white border border-white/30 shadow-xs transition-all cursor-pointer disabled:opacity-50"
              >
                {isRequesting ? (
                  <RefreshCw className="w-3 h-3 animate-spin" />
                ) : (
                  <Send className="w-3 h-3" />
                )}
                <span>Request Admin to Activate</span>
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default LicenseBanner;

